/**
 * File: documentKey.ts
 * Description: Canonical identity for knowledge-base documents so the same
 *   guideline is recognised whether it came from search (encoded index id) or
 *   from the blob listing (blob name).
 */

const GUIDELINES_BLOB_PREFIX = 'archie-guidelines/';
const SEARCH_DOCUMENT_PATH_PREFIX = '/documents/';
// Azure Search keys use URL-token base64: a trailing digit is the padding count.
const URL_TOKEN_BASE64_PATTERN = /^([A-Za-z0-9_-]+)([0-2])$/;
const HTTP_URL_PATTERN = /^https?:\/\//;

const decodeBase64 = (value: string): string | null => {
  try {
    return atob(value);
  } catch {
    return null;
  }
};

const decodeEncodedBlobUrl = (value: string): string | null => {
  const token = URL_TOKEN_BASE64_PATTERN.exec(value);
  const decoded = token
    ? decodeBase64(
      token[1].replaceAll('-', '+').replaceAll('_', '/') + '='.repeat(Number(token[2])),
    )
    : decodeBase64(value);
  return decoded && HTTP_URL_PATTERN.test(decoded) ? decoded : null;
};

/**
 * Whether a canonical key refers to a file in the guidelines blob folder.
 * @param key - A value returned by toCanonicalDocumentKey
 * @return True for guideline blob names
 */
export const isGuidelineBlobKey = (key: string): boolean =>
  key.startsWith(GUIDELINES_BLOB_PREFIX);

/**
 * Reduce a document path/id to a stable key. Encoded blob URLs are decoded and
 * guideline blobs collapse to their blob name, matching the blob listing ids.
 * @param value - The document path or id
 * @return A key that is equal for the same underlying file
 */
export const toCanonicalDocumentKey = (value: string): string => {
  const encoded = value.startsWith(SEARCH_DOCUMENT_PATH_PREFIX)
    ? value.slice(SEARCH_DOCUMENT_PATH_PREFIX.length)
    : value;
  const decoded = decodeEncodedBlobUrl(encoded) ?? value;

  const prefixIndex = decoded.indexOf(GUIDELINES_BLOB_PREFIX);
  if (prefixIndex < 0) {
    return decoded;
  }

  const blobName = decoded.slice(prefixIndex);
  if (!HTTP_URL_PATTERN.test(decoded)) {
    return blobName;
  }
  try {
    return decodeURIComponent(blobName);
  } catch {
    return blobName;
  }
};
